import { NotificationType } from '@api/READ_ONLY/notifications_api/Api';
import { Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { FiAlertTriangle } from 'react-icons/fi';
import { DeleteShiftNotificationConflictType, UpdateShiftConflictNotificationType } from '../../types/notifications';
import { notificationSentAtFormattedDate } from '../../utils';
import { inDisplayZone } from '@app/utils/dates/displayTimezone';

type ShiftConflictNotificationCardProps = {
  notificationDetails: DeleteShiftNotificationConflictType | UpdateShiftConflictNotificationType;
  onClick: () => void;
};

/** How many conflicting visits the card lists before it summarises the rest. */
const VISITS_SHOWN = 3;

/**
 * Visits left unassigned by a shift edit.
 *
 * This one is addressed to the ADMIN who edited the shift - the only
 * notification in the product that is - and it went nowhere: neither selector
 * had a case for it, so the office got a browser push and then an empty space
 * where the message should have been.
 *
 * The visits are rendered from `metadata`, not from `message`. The backend
 * template pastes the same list into the body as text, with raw ISO timestamps
 * ("Client: Πέτρος Χριστοδούλου, 2025-12-18T19:00+02:00 Tatoiou 12"), so
 * showing both would print the same visits twice and the uglier copy first.
 * The lead line comes from the TYPE rather than from that body, which is also
 * what makes it translatable.
 */
const ShiftConflictNotificationCard = ({ notificationDetails, onClick }: ShiftConflictNotificationCardProps) => {
  const { t } = useTranslation();

  const visits = notificationDetails.metadata ?? [];
  const shown = visits.slice(0, VISITS_SHOWN);
  const remaining = visits.length - shown.length;

  const lead =
    notificationDetails.type === NotificationType.DeleteShiftConflict
      ? t('notifications-card-shift-conflict-deleted')
      : t('notifications-card-shift-conflict-updated');

  return (
    <div
      onClick={onClick}
      className={
        'flex items-start gap-4 bg-white shadow-sm rounded-[10px] p-4 border border-gray-200 hover:shadow-md transition-shadow cursor-pointer'
      }
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
        <FiAlertTriangle size={22} />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between">
          <div className="min-w-0 mb-1">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-gray-900 line-clamp-2">{notificationDetails.title}</h3>
              {visits.length > 0 && (
                <Tag color="warning">{t('notifications-shift-conflict-visit-count', { count: visits.length })}</Tag>
              )}
            </div>

            <p className="mt-1 text-sm text-gray-700">{lead}</p>
          </div>

          <div className="ml-4 flex shrink-0 flex-col items-end gap-4">
            <span
              className={`inline-block h-2.5 w-2.5 rounded-full ${notificationDetails.isRead ? 'bg-gray-300' : 'bg-amber-600'}`}
            />

            <div className="text-right text-xs text-gray-500">
              {t('notifications-sent-at', { time: notificationSentAtFormattedDate(notificationDetails.sentAt) })}
            </div>
          </div>
        </div>

        {shown.length > 0 && (
          <ul className="mt-2 flex flex-col gap-1">
            {shown.map((visit) => (
              <li key={visit.visitId} className="rounded-md bg-amber-50 px-3 py-1.5 text-xs text-amber-900">
                <span className="font-medium">
                  {[visit.clientFirstName, visit.clientLastName].filter(Boolean).join(' ')}
                </span>
                {visit.visitStartTime && (
                  <span> · {inDisplayZone(visit.visitStartTime).format('DD MMM YYYY, HH:mm')}</span>
                )}
                {visit.address && <span className="text-amber-800"> · {visit.address}</span>}
              </li>
            ))}
          </ul>
        )}

        {remaining > 0 && (
          <div className="mt-1 text-xs text-gray-500">
            {t('notifications-shift-conflict-more-visits', { count: remaining })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShiftConflictNotificationCard;
